import { Grid } from '@mui/material';
import React,{ PropsWithChildren } from 'react';
import TruncatedTypographicText from 'src/components/truncated-typographic-text';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import Storage from 'src/utils/storage/storage';
import usePopper from '../../../planner_v2/hooks/popper-hook';
import { toggleDrawerView } from '../../../planner_v2/store/planner-slice';
import DRAWER_VIEW_TYPE from '../../../planner_v2/types/drawer-view-type';
import { IGridCell } from '../../../planner_v2/types/grid-x-to-y-response.model';
import { ToBeMetric } from '../../types/grid-color-gradient-map.model';
import MetricsPopper from './metrics-popper';
import ItemDetails from './ppg-details';

interface IGridDatumBoxProps {
  /**
   * List of SKUs to show in a grid cell for a SKU view
   */
  items: IGridCell[];

  // eslint-disable-next-line react/require-default-props
  toBeMetric?: ToBeMetric;
}

/**
 * A wrapper component for a grid cell
 */
function GridDatumWrapper({
  isEmpty,
  children
}: PropsWithChildren<{ isEmpty: boolean }>) {
  return (
    <Grid
      container
      alignContent='flex-start'
      className='row-item'
      sx={{
        overflowY: 'auto',
        maxHeight: '100%',
        backgroundColor: isEmpty ? 'transparent' : '#FFFFFF',
        border: '1px solid #D9D9D9',
        padding: (theme) => theme.spacing(1)
      }}
    >
      {children}
    </Grid>
  );
}

function ChildItemDetails({
  item,
  parentItemId,
  isClickable,
  isHover,
  productKeyRef = '',
  isOpportunityVisible = false
}: {
  /**
   * Details of the SKU
   */
  item: IGridCell;
  /**
   * Id of the PPG this SKU belongs to, required in order to pass to drawer when clicked
   */
  parentItemId: number;
  /**
   * Is the item clickable (does it open drawer?)
   */
  isClickable: boolean;
  /**
   * Is the metric visible when cursor hovers over it
   */
  isHover: boolean;
  /**
   * Key name to pointing to the product name/id etc
   */
  productKeyRef: string;
  /**
   * Is opportunity grid visible
   */
  isOpportunityVisible: boolean;
}) {
  const appDispatch = useAppDispatch();
  const { opportunityToColorMap } = useAppSelector(
    (state) => state.simulatorHome
  );

  // getting the ph recommendation map config data from local storage (as it will be accessible on refresh as well)
  const phRecommendationConfig = Storage.getPhRecommendationLegend();

  // getting the view ph recommendation map config data from scenario state
  const { isViewPhRecommendation } = useAppSelector((state) => state.plannerV2);

  const { handleMouseEnter, handleMouseLeave, anchorEl, isPopperOpen } =
    usePopper({ showPopper: isHover });
  return (
    <Grid
      container
      alignItems='center'
      wrap='nowrap'
      item
      xs={12}
      sx={{ my: '2px' }}
    >
      {' '}
      {isOpportunityVisible ? (
        <Grid
          item
          container
          sx={{
            width: '40px',
            height: '10px',
            borderRadius: '2px',
            backgroundColor: '#E8E8E8',
            mr: 1,
            overflow: 'hidden'
          }}
        >
          {(item.parent.data.opportunities || []).map((opportunityId) => (
            <Grid
              key={opportunityId}
              item
              sx={{
                width: `${100 / item.parent.data.opportunities.length}%`,
                height: '10px',
                backgroundColor:
                  opportunityToColorMap.find(
                    (opp) =>
                      opp.pk_id === opportunityId ||
                      opp.intermediate_opp_id === opportunityId
                  )?.color || '#E8E8E8'
              }}
            />
          ))}
        </Grid>
      ) : null}
      <Grid
        item
        xs
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <TruncatedTypographicText
          fontSize='10px'
          fontWeight={400}
          textTransform='uppercase'
          color={item.parent.data.opportunities.length ? 'black' : '#777777'}
          sx={
            isHover
              ? {
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: (theme) => theme.palette.primary.main
                  }
                }
              : {}
          }
          onClick={() => {
            if (isClickable) {
              appDispatch(
                toggleDrawerView({
                  view: DRAWER_VIEW_TYPE.PRODUCT_DETAILS,
                  productId: item.parent.data[item.parent.data.key],
                  productKeyRef
                })
              );
            }
          }}
        >
          <div
            style={
              isViewPhRecommendation &&
              phRecommendationConfig &&
              phRecommendationConfig[item.parent.data.ph_recommendation]
                ? {
                    color:
                      phRecommendationConfig[item.parent.data.ph_recommendation]
                        .color,
                    fontWeight: 'bold'
                  }
                : {}
            }
          >
            {item.parent.data[`${item.parent.data.key}_name`]}
          </div>
        </TruncatedTypographicText>
        <MetricsPopper
          metrics={item.parent.data['hover-metric']}
          open={isPopperOpen}
          anchorEl={anchorEl}
        />
      </Grid>
    </Grid>
  );
}

/**
 * Renders the details for a parent and its children in a grid cell
 */
export default function GridDatumBox({
  items,
  toBeMetric = null
}: IGridDatumBoxProps) {
  return (
    <GridDatumWrapper isEmpty={!items.length}>
      {items.map((parentItem, index) => {
        const isProductMarkedForOpportunity = parentItem.children.some(
          (child) => !!child.parent.data.opportunities.length
        );
        const metricValue = parentItem.parent.data['gradient-metric']
          ? parentItem.parent.data['gradient-metric'].find(
              (metric) => metric.kpi === toBeMetric.name
            )
          : null;
        return (
          <React.Fragment key={parentItem.parent.data[`${parentItem.parent.data.key}_name`]}>
            <ItemDetails
              metricColors={toBeMetric?.gradient_color_metadata}
              productKeyRef={parentItem.parent.data.key}
              opportunities={parentItem.parent.data.opportunities}
              isMarkedForOpportunity={isProductMarkedForOpportunity}
              isOpportunityVisible={parentItem.parent.data.show_opportunity}
              productId={parentItem.parent.data[parentItem.parent.data.key]}
              metrics={parentItem.parent.data['hover-metric']}
              isHover={parentItem.parent.data.is_hover}
              isClickable={parentItem.parent.data.is_clickable}
              price={parentItem.parent.data['item-metric']}
              metricValue={metricValue?.value || 0}
              metricMaxValue={toBeMetric?.max}
              metricMinValue={toBeMetric?.min}
              view={DRAWER_VIEW_TYPE.SKU}
              name={
                parentItem.parent.data[`${parentItem.parent.data.key}_name`]
              }
              imgUrl={
                parentItem.parent.data[
                  `${parentItem.parent.data.key}_image_url`
                ]
              }
            />
            {parentItem.children.map((child) => (
              <ChildItemDetails
                isClickable={child.parent.data.is_clickable}
                isHover={child.parent.data.is_hover}
                isOpportunityVisible={child.parent.data.show_opportunity}
                productKeyRef={child.parent.data.key}
                parentItemId={
                  parentItem.parent.data[parentItem.parent.data.key]
                }
                item={child}
                key={child.parent.data[`${parentItem.parent.data.key}_name`]}
              />
            ))}
            {index !== items.length - 1 ? (
              <Grid
                item
                xs={12}
                my={1}
                sx={{ height: '1px', backgroundColor: '#D9D9D9' }}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </GridDatumWrapper>
  );
}
